/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, ModalHeader, Modal, ModalBody, FormGroup } from 'reactstrap';
import { reloadInitiative } from '../../actions/initiative';
import { ROUTES } from '../../constants/routes';
import { RootState } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { BlockingLoader } from '../loader/BlockingLoader';
import './SdgContributionShareModal.scss';

export interface ShareModalProps {
  isOpen: boolean;
  toggle: () => void;
  handleDownloadImage: () => void;
}

export const SdgContributionShareModal = (props: ShareModalProps) => {
  const { isOpen, toggle, handleDownloadImage } = props;

  const dispatch = useDispatch();
  const initiativeState = useSelector((state: RootState) => state.initiative);
  const initiative = initiativeState.data;
  const liveSharing = initiativeState.loaded && initiative?.displaySettings?.sdgContributionChart?.liveSharing === true
  const [saving, setSaving] = useState(false);

  if (!initiativeState.loaded || !isOpen || !initiative) {
    return <></>;
  }

  const initiativeId = initiative._id;
  const token = initiative?.displaySettings?.sdgContributionChart?.token;

  const handleToggleLiveSharing = () => {
    setSaving(true);
    G17Client.updateDisplaySettings(initiativeId, {
      sdgContributionChart: {
        liveSharing: !liveSharing
      }
    })
      .then(() => dispatch(reloadInitiative()))
      .then(() => setSaving(false))
      .catch(() => setSaving(false));
  }

  const handleCopy = (copyText: string, event: AnalyticsEvents) => {
    getAnalytics().track(event, { initiativeId })
    return navigator.clipboard.writeText(copyText)
  }

  const disabled = !liveSharing || saving;

  const { host, protocol } = window.location;
  const href = `${protocol}//${host}`;
  const url = token ? `${href}${generateUrl(ROUTES.PUBLIC_SDG_CHART, { initiativeId, token })}` : href;
  const iframeCode = `<iframe width="966" height="444" src="${url}"
  title="SDG Contributions"
  frameborder = "0"></iframe>`;

  return <Modal className='share-modal' isOpen={true} toggle={toggle} backdrop='static'>
    <ModalHeader toggle={toggle}>
      Share
    </ModalHeader>
    <ModalBody>
      <div className='d-flex justify-content-end align-items-center'>
        <div className='share-toggle text-sm'>Allow sharing</div>
        <div className='ml-2'>
          {saving
            ?
            <BlockingLoader />
            :
            <FormGroup switch>
              <Input
                type='switch'
                name={'liveSharing'}
                checked={liveSharing}
                id='liveSharingSwitch'
                onChange={handleToggleLiveSharing}
              />
            </FormGroup>
          }
        </div>
      </div>

      <h5 className='mt-0'>Link</h5>
      <div className={`share-modal-link ${disabled ? 'disabled' : ''}`}>
        <div>
          <p onCopy={() => handleCopy(url, AnalyticsEvents.SDGGraphEmbed)}>
            {url}
          </p>
          <div className='text-right'>
            <i className='fa fa-copy clickable'
              onClick={() => !disabled && handleCopy(url, AnalyticsEvents.SDGGraphLink)} />
          </div>
        </div>
      </div>

      <h5 className='mt-3'>Embed</h5>
      <div className={`share-modal-embed ${disabled ? 'disabled' : ''}`}>
        <div>
          <p onCopy={() => handleCopy(iframeCode, AnalyticsEvents.SDGGraphEmbed)} >
            {iframeCode}
          </p>
          <div className='text-right'>
            <i className='fa fa-copy clickable'
              onClick={() => !disabled && handleCopy(iframeCode, AnalyticsEvents.SDGGraphEmbed)} />
          </div>
        </div>
      </div>

      <h5 className='mt-3'>Download Image</h5>
      <div className='share-modal-download'>
        <Button color='link' onClick={handleDownloadImage}>
        <i className='mr-2 fa fa-cloud-download-alt' />SDG Contribution graph
        </Button>
      </div>
    </ModalBody>
  </Modal >
}
